import type { License } from '../../types/domain';
import { ApiClient } from '../config';

export interface LicenseService {
  getAllLicenses(customerId?: string): Promise<License[]>;
  getLicense(id: string): Promise<License>;
  createLicense(data: Partial<License>): Promise<License>;
  updateLicense(id: string, data: Partial<License>): Promise<License>;
  deleteLicense(id: string): Promise<void>;
  revokeLicense(id: string): Promise<License>;
  reactivateLicense(id: string): Promise<void>;
}

export class LicenseServiceImpl implements LicenseService {
  private apiClient: ApiClient;

  constructor(apiClient: ApiClient) {
    this.apiClient = apiClient;
  }

  private transformLicense(license: any): License {
    console.log('[LicenseService] Transforming license:', license);

    // Parse moduleIds/modules - handle JSON string, array, or undefined
    let modules: string[] = [];
    if (Array.isArray(license.modules)) {
      modules = license.modules;
    } else if (Array.isArray(license.moduleIds)) {
      modules = license.moduleIds;
    } else if (typeof license.moduleIds === 'string' && license.moduleIds) {
      try {
        const parsed = JSON.parse(license.moduleIds);
        modules = Array.isArray(parsed) ? parsed : [];
      } catch {
        modules = [];
      }
    } else if (typeof license.modules === 'string' && license.modules) {
      try {
        const parsed = JSON.parse(license.modules);
        modules = Array.isArray(parsed) ? parsed : [];
      } catch {
        modules = [];
      }
    }

    console.log('[LicenseService] modules result:', modules, 'isArray:', Array.isArray(modules), 'contents:', JSON.stringify(modules));

    const transformed = {
      ...license,
      modules,
    };
    console.log('[LicenseService] Transformed license modules:', transformed.modules);
    return transformed;
  }

  private transformToApi(data: Partial<License>) {
    const { modules, ...rest } = data;
    return {
      ...rest,
      moduleIds: modules
    };
  }

  async getAllLicenses(customerId?: string): Promise<License[]> {
    console.log('[LicenseService] Fetching all licenses. CustomerId:', customerId);
    try {
      const params = customerId ? { customerId } : undefined;
      const licenses = await this.apiClient.get<any[]>('/api/v1/licenses', params);
      console.log('[LicenseService] Raw licenses:', licenses);
      
      const transformedLicenses = licenses.map(license => this.transformLicense(license));
      console.log('[LicenseService] Transformed licenses:', transformedLicenses);
      
      return transformedLicenses;
    } catch (error) {
      console.error('[LicenseService] Error fetching licenses:', error);
      throw error;
    }
  }

  async getLicense(id: string): Promise<License> {
    try {
      const license = await this.apiClient.get<any>(`/api/v1/licenses/${id}`);
      return this.transformLicense(license);
    } catch (error) {
      console.error('[LicenseService] Error fetching license:', error);
      throw error;
    }
  }

  async createLicense(data: Partial<License>): Promise<License> {
    try {
      const apiData = this.transformToApi(data);
      const license = await this.apiClient.post<any>('/api/v1/licenses', apiData);
      return this.transformLicense(license);
    } catch (error) {
      console.error('[LicenseService] Error creating license:', error);
      throw error;
    }
  }

  async updateLicense(id: string, data: Partial<License>): Promise<License> {
    try {
      const apiData = this.transformToApi(data);
      // Use PATCH instead of PUT to match the backend route
      const license = await this.apiClient.patch<any>(`/api/v1/licenses/${id}`, apiData); 
      return this.transformLicense(license);
    } catch (error) {
      console.error('[LicenseService] Error updating license:', error);
      throw error;
    }
  }

  async deleteLicense(id: string): Promise<void> {
    try {
      await this.apiClient.delete(`/api/v1/licenses/${id}`);
    } catch (error) {
      console.error('[LicenseService] Error deleting license:', error);
      throw error;
    }
  }

  async revokeLicense(id: string): Promise<License> {
    console.log('[LicenseService] Revoking license:', id);
    try {
      // The revoke endpoint doesn't require a body, but the apiClient method expects one.
      const license = await this.apiClient.patch<any>(`/api/v1/licenses/${id}/revoke`, {}); 
      return this.transformLicense(license);
    } catch (error) {
      console.error('[LicenseService] Error revoking license:', error);
      throw error;
    }
  }

  async reactivateLicense(id: string): Promise<void> {
    console.log('[LicenseService] Reactivating license:', id);
    try {
      // The reactivate endpoint doesn't require a body, but the apiClient method expects one.
      await this.apiClient.post(`/api/v1/licenses/${id}/reactivate`, {});
    } catch (error) {
      console.error('[LicenseService] Error reactivating license:', error);
      throw error;
    }
  }
}

export const licenseService = new LicenseServiceImpl(new ApiClient());
